import { useCallback, useMemo } from 'react';

import useAuthGuardContext from './useAuthGuardContext.js';

export type AuthGuardHookOptions = {
  /**
   * The scopes that the user have, if it's empty, it will gets the scopes from the context
   */
  scopes: string[];
  /**
   * If true, it will throw an error if the user doesn't have the right scope
   */
  raiseError?: boolean;
};

/**
 * Checks if the user has the right scope to access
 */
export const useAuthGuard = (opts?: AuthGuardHookOptions) => {
  const ctx = useAuthGuardContext(false);

  const hasScope = useCallback(
    (scope: string | string[]) => {
      const scopes = Array.isArray(scope) ? scope : [scope];

      const granted = scopes.every((scope) => {
        return opts?.scopes.includes(scope) ?? false;
      });

      if (!granted && (opts?.raiseError ?? true)) {
        throw new Error(
          `Unauthorized, missing scopes: "${scopes.join(', ')}" for available: "${opts?.scopes.join(
            ', '
          )}"`
        );
      }

      return granted;
    },
    [opts?.raiseError, opts?.scopes]
  );

  return useMemo(
    () => ({
      hasScope: ctx?.hasScope ?? hasScope,
    }),
    [ctx?.hasScope, hasScope]
  );
};

export default useAuthGuard;
